import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useToast } from "../../hooks/use-toast";
import { api } from "../../lib/api";
import type { Entity } from "../../lib/types";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

interface EntityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entity: Entity | null;
  onSave: () => void;
}

export const EntityDialog = ({
  open,
  onOpenChange,
  entity,
  onSave,
}: EntityDialogProps) => {
  const { toast } = useToast();

  const form = useForm({
    defaultValues: {
      name: "",
      type: "pharmacy",
      branch_id: "",
      client_id: "",
      line_1: "",
      line_2: "",
      pincode: "",
      latitude: "",
      longitude: "",
    },
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (entity) {
        form.reset({
          name: entity.name,
          type: entity.type,
          branch_id: entity.branch_id?.toString() || "",
          client_id: entity.client_id?.toString() || "",
          line_1: entity.metadata?.line_1 || "",
          line_2: entity.metadata?.line_2 || "",
          pincode: entity.metadata?.pincode || "",
          latitude: entity.metadata?.lat || "",
          longitude: entity.metadata?.lng || "",
        });
      } else {
        form.reset({
          name: "",
          type: "pharmacy",
          branch_id: "",
          client_id: "",
          line_1: "",
          line_2: "",
          pincode: "",
          latitude: "",
          longitude: "",
        });
      }
    }
  }, [entity, form, open]);

  const onSubmit = async (data: any) => {
    try {
      // Preserve existing metadata and append new values
      const metadata = {
        ...(entity?.metadata || {}),
        line_1: data.line_1,
        line_2: data.line_2,
        pincode: data.pincode,
        lat: data.latitude,
        lng: data.longitude,
      };

      const entityData = {
        name: data.name,
        type: data.type,
        branch_id: data.branch_id,
        client_id: data.client_id,
        metadata,
      };

      if (entity) {
        await api.updateEntity(entity.id || 0, entityData);
        toast({
          title: "Entity Updated",
          description: `${data.name} details updated`,
          variant: "default",
        });
      } else {
        await api.createEntity(entityData);
        toast({
          title: "Entity Created",
          description: `${data.name} details created`,
          variant: "default",
        });
      }
      onSave();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to save entity:", error);
      toast({
        title: "Failed to save entity",
        description: `${error}`,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{entity ? "Edit Entity" : "Create Entity"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={!!entity}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="pharmacy">Pharmacy</SelectItem>
                      <SelectItem value="entity">Entity</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="branch_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Branch ID</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} min={1} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="client_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client ID</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} min={1} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="line_1"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 1</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="line_2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 2</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pincode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pincode</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="latitude"
                rules={{ required: "Latitude is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Latitude*</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g. 28.6139"
                        type="number"
                        step="any"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="longitude"
                rules={{ required: "Longitude is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Longitude*</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g. 77.2090"
                        type="number"
                        step="any"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button type="submit">Save</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
